import nspell from "nspell";

// Términos propios de la empresa/negocio que no están en el diccionario
// general de español pero son correctos tal cual. Amplía esta lista según
// se detecten falsos positivos recurrentes (nombres de clientes, marcas,
// siglas técnicas, etc.).
export const PALABRAS_EXCEPCION = [
  "Netjer",
  "Netjernetworks",
  "Rexten",
  "BOM",
  "SOW",
  "Wi-Fi",
  "WiFi",
  "AP",
  "APs",
  "kick-off",
];

// Incluye guiones internos para no partir términos compuestos como
// "kick-off" o "Wi-Fi" en dos palabras sueltas.
const REGEX_PALABRA = /[A-Za-zÀ-ÖØ-öø-ÿ]+(?:-[A-Za-zÀ-ÖØ-öø-ÿ]+)*/g;

// Palabras que el propio usuario añadió desde la ventana de revisión
// ortográfica (persisten en este navegador, sin tocar código).
const CLAVE_ALMACENAMIENTO = "docgen_diccionario_personalizado";

export const obtenerPalabrasPersonalizadas = () => {
  try {
    const guardado = window.localStorage.getItem(CLAVE_ALMACENAMIENTO);
    const lista = guardado ? JSON.parse(guardado) : [];
    return Array.isArray(lista) ? lista : [];
  } catch (error) {
    console.error("No se pudo leer el diccionario personalizado.", error);
    return [];
  }
};

const guardarPalabrasPersonalizadas = (palabras) => {
  try {
    window.localStorage.setItem(CLAVE_ALMACENAMIENTO, JSON.stringify(palabras));
  } catch (error) {
    console.error("No se pudo guardar el diccionario personalizado.", error);
  }
};

// Añade una palabra al diccionario: la reconoce de inmediato en el
// corrector ya cargado (para el resto de esta revisión) y la persiste en
// localStorage para que futuras sesiones no vuelvan a marcarla.
export const agregarPalabraAlDiccionario = (spell, palabra) => {
  const limpia = (palabra || "").trim();
  if (!limpia) return;
  spell.add(limpia);
  const actuales = obtenerPalabrasPersonalizadas();
  if (!actuales.includes(limpia)) {
    guardarPalabrasPersonalizadas([...actuales, limpia]);
  }
};

let corrPromesa = null;

// Carga (una sola vez) el diccionario Hunspell español y crea la instancia
// de nspell. Los archivos .aff/.dic se sirven como assets estáticos desde
// /public/dictionaries, así que no requieren empaquetarse con webpack.
export const cargarCorrector = () => {
  if (!corrPromesa) {
    const base = process.env.PUBLIC_URL || "";
    corrPromesa = Promise.all([
      fetch(`${base}/dictionaries/es.aff`).then((r) => r.text()),
      fetch(`${base}/dictionaries/es.dic`).then((r) => r.text()),
    ]).then(([aff, dic]) => {
      const spell = nspell({ aff, dic });
      PALABRAS_EXCEPCION.forEach((palabra) => spell.add(palabra));
      obtenerPalabrasPersonalizadas().forEach((palabra) => spell.add(palabra));
      return spell;
    });
  }
  return corrPromesa;
};

// Aplica el patrón de mayúsculas/minúsculas de `original` a `sugerencia`,
// para no reemplazar "Recuros" por "recursos" (perdiendo la mayúscula
// inicial) o "RECUROS" por "recursos" en un texto todo en mayúsculas.
const aplicarCapitalizacion = (sugerencia, original) => {
  if (!sugerencia) return sugerencia;
  if (original === original.toUpperCase()) return sugerencia.toUpperCase();
  if (original[0] === original[0].toUpperCase()) {
    return sugerencia[0].toUpperCase() + sugerencia.slice(1);
  }
  return sugerencia;
};

// Revisa un texto y devuelve la lista de palabras no reconocidas por el
// corrector, cada una con hasta 5 sugerencias (ya capitalizadas acorde al
// original). No repite la misma palabra dos veces.
export const revisarTexto = (spell, texto) => {
  if (!texto || typeof texto !== "string") return [];
  const encontradas = new Map();
  const coincidencias = texto.match(REGEX_PALABRA) || [];

  coincidencias.forEach((palabra) => {
    if (palabra.length < 2) return;
    if (encontradas.has(palabra)) return;
    if (spell.correct(palabra)) return;

    const sugerencias = spell
      .suggest(palabra)
      .slice(0, 5)
      .map((sugerencia) => aplicarCapitalizacion(sugerencia, palabra));

    encontradas.set(palabra, sugerencias);
  });

  return Array.from(encontradas, ([palabra, sugerencias]) => ({
    palabra,
    sugerencias,
  }));
};

// Reemplaza, en todo el texto, cada ocurrencia de las palabras clave del
// mapa `correcciones` (palabra original -> palabra corregida) respetando
// límites de palabra completa.
export const aplicarCorreccionesTexto = (texto, correcciones) => {
  if (!texto || typeof texto !== "string") return texto;
  if (!correcciones || correcciones.size === 0) return texto;

  let resultado = texto;
  correcciones.forEach((reemplazo, palabraOriginal) => {
    if (reemplazo === palabraOriginal) return;
    const escapada = palabraOriginal.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`\\b${escapada}\\b`, "g");
    resultado = resultado.replace(regex, reemplazo);
  });
  return resultado;
};
